import type { ResolvedCalendar } from '../calendar/capacity';
import type { WorkCalendar } from '../calendar/work-calendar';
import {
  endAfterWorkingDays,
  firstWorkingDayFrom,
  nextWorkingDayAfter,
} from '../calendar/working-days';
import { BusinessDate } from '../date/business-date';
import type { DomainError } from '../shared/domain-error';
import type {
  ActivityId,
  DisciplineId,
  FloorId,
  FloorPlanId,
  ProjectId,
  ServiceId,
  UnitTypeId,
  UserId,
} from '../shared/ids';
import { ok, type Result } from '../shared/result';
import { resolveItemQuantity, type TakeoffQuantities } from '../takeoff/consolidation';
import { isHourUnit } from '../takeoff/labor-rate';
import type { CatalogService } from '../takeoff/service-catalog';
import { isAllocatable, type Employee } from '../workforce/employee';
import { JobRoleDisciplineIndex, type JobRoleDiscipline } from '../workforce/job-role-discipline';
import {
  createScheduleItem,
  SCHEDULE_ITEM_DECIMALS,
  type ScheduleItem,
  type ScheduleItemChange,
  type ScheduleItemId,
} from './schedule-item';
import { roundQuantity } from './unit-of-measure';

/**
 * O cronograma a partir do quantitativo (Fase 31, RN-415 a RN-420).
 *
 * O quantitativo já sabe o que o cronograma precisa — a quantidade de cada serviço por
 * tipologia, os pavimentos de cada tipologia pelas posições no desenho e o HH dos itens de
 * mão de obra —, e o "Cronograma por item" só nascia de arquivo. Aqui mora a conta pura que
 * liga os dois:
 *
 * ```text
 * tipologia × serviço ──posições do desenho do pavimento──► pavimento × serviço (RN-415)
 *                       duração = teto(HH ÷ (pessoas × jornada)) (RN-416)
 *                       linha de balanço: pavimento a pavimento, serviço a serviço (RN-417)
 * ```
 *
 * Nada aqui lê banco: o caso de uso entrega o cadastro, o calendário e o quantitativo já
 * lidos, e recebe a proposta, a diferença e os itens prontos para gravar.
 */

// ── O quantitativo por pavimento (RN-415) ───────────────────────────────────────

/**
 * O número de um serviço numa tipologia, como o quantitativo o mostra.
 *
 * A quantidade é a da unidade do serviço (RN-232); o HH é a soma dos itens de mão de obra
 * medidos em hora (`serviceLaborHours`). Ausente é "o quantitativo não tem número", e não
 * zero.
 */
export interface TypologyServiceTakeoff {
  readonly unitTypeId: UnitTypeId;
  readonly serviceId: ServiceId;
  readonly quantity?: number;
  readonly laborHours?: number;
}

/** O pavimento com o desenho que ele aponta (`floors.floorPlanId`, RN-167). */
export interface TakeoffFloor {
  readonly id: FloorId;
  readonly floorPlanId?: FloorPlanId;
  readonly deletedAt?: string;
}

/** A posição de uma tipologia no desenho — `count` vezes (a vaga repetida). */
export interface TakeoffPosition {
  readonly floorPlanId: FloorPlanId;
  readonly unitTypeId: UnitTypeId;
  readonly count: number;
  readonly deletedAt?: string;
}

/** O que a linha precisa saber do serviço: a disciplina (a frente) e a unidade. */
export interface TakeoffService {
  readonly disciplineId: DisciplineId;
  readonly unit?: string;
}

/** Uma linha do quantitativo por pavimento × serviço — o que vira um item (RN-415). */
export interface TakeoffScheduleLine {
  readonly floorId: FloorId;
  readonly disciplineId: DisciplineId;
  readonly serviceId: ServiceId;
  readonly quantity?: number;
  readonly unit?: string;
  readonly laborHours?: number;
}

/** A chave pavimento × serviço — a do item sem trecho que o quantitativo gera. */
export function takeoffLineKey(floorId: FloorId | string, serviceId: ServiceId | string): string {
  return `${floorId}|${serviceId}`;
}

const arredondar = (valor: number): number => roundQuantity(valor, SCHEDULE_ITEM_DECIMALS);

interface Soma {
  quantity: number;
  temQuantidade: boolean;
  laborHours: number;
  temHh: boolean;
}

/**
 * RN-415 — o quantitativo de cada pavimento × serviço.
 *
 * A quantidade do pavimento é a soma, nas posições do desenho que ele aponta, da quantidade
 * da tipologia de cada posição — vezes quantas vezes a posição existe. Dois pavimentos que
 * apontam o mesmo desenho recebem o mesmo número; o pavimento sem desenho não tem
 * quantitativo. O HH segue a mesma soma.
 *
 * Só entra o pavimento × serviço com alguma coisa a fazer: quantidade ou HH maior que zero.
 * O serviço que a tipologia não usa vem zerado, e um item sem nada seria uma barra vazia.
 *
 * A linha de serviço que o mapa `services` não conhece fica de fora — quem chama já a
 * reportou como serviço que a obra não alcança.
 */
export function takeoffLinesByFloor(input: {
  readonly floors: readonly TakeoffFloor[];
  readonly positions: readonly TakeoffPosition[];
  readonly typologies: readonly TypologyServiceTakeoff[];
  readonly services: ReadonlyMap<string, TakeoffService>;
}): TakeoffScheduleLine[] {
  const porTipologia = new Map<string, TypologyServiceTakeoff[]>();
  for (const linha of input.typologies) {
    const lista = porTipologia.get(linha.unitTypeId) ?? [];
    lista.push(linha);
    porTipologia.set(linha.unitTypeId, lista);
  }
  const posicoesPorDesenho = new Map<string, TakeoffPosition[]>();
  for (const posicao of input.positions) {
    if (posicao.deletedAt !== undefined) continue;
    const lista = posicoesPorDesenho.get(posicao.floorPlanId) ?? [];
    lista.push(posicao);
    posicoesPorDesenho.set(posicao.floorPlanId, lista);
  }

  const linhas: TakeoffScheduleLine[] = [];
  for (const floor of input.floors) {
    if (floor.deletedAt !== undefined || floor.floorPlanId === undefined) continue;

    const somas = new Map<string, Soma>();
    for (const posicao of posicoesPorDesenho.get(floor.floorPlanId) ?? []) {
      const vezes = Number.isFinite(posicao.count) && posicao.count > 0 ? posicao.count : 0;
      if (vezes === 0) continue;
      for (const linha of porTipologia.get(posicao.unitTypeId) ?? []) {
        const soma = somas.get(linha.serviceId) ?? {
          quantity: 0,
          temQuantidade: false,
          laborHours: 0,
          temHh: false,
        };
        if (linha.quantity !== undefined && Number.isFinite(linha.quantity)) {
          soma.quantity += linha.quantity * vezes;
          soma.temQuantidade = true;
        }
        if (linha.laborHours !== undefined && Number.isFinite(linha.laborHours)) {
          soma.laborHours += linha.laborHours * vezes;
          soma.temHh = true;
        }
        somas.set(linha.serviceId, soma);
      }
    }

    for (const [serviceId, soma] of somas) {
      const service = input.services.get(serviceId);
      if (service === undefined) continue;
      const quantity = soma.temQuantidade ? arredondar(soma.quantity) : undefined;
      const laborHours = soma.temHh ? arredondar(soma.laborHours) : undefined;
      if (!((quantity ?? 0) > 0 || (laborHours ?? 0) > 0)) continue;
      linhas.push({
        floorId: floor.id,
        disciplineId: service.disciplineId,
        serviceId: serviceId as ServiceId,
        ...(quantity === undefined || quantity === 0 ? {} : { quantity }),
        ...(quantity === undefined || quantity === 0 || service.unit === undefined
          ? {}
          : { unit: service.unit }),
        ...(laborHours === undefined ? {} : { laborHours }),
      });
    }
  }
  return linhas;
}

/**
 * O HH de um serviço numa tipologia — o que a tela do quantitativo manda em `laborHours`.
 *
 * É a soma dos itens de **mão de obra** do serviço medidos em hora (`isHourUnit`: a unidade
 * ausente é hora, como na consolidação, RN-166), cada um com a quantidade que o quantitativo
 * resolve para ele — digitada, das peças importadas, calculada ou da planilha
 * (`resolveItemQuantity`, D9, RN-384). É a mesma conta que a consolidação e o orçamento fazem,
 * serviço a serviço.
 *
 * O item em "dia" ou "mês" não entra: somar dia com hora daria um HH que ninguém estranharia.
 * Sem item de mão de obra com número, `undefined` — o serviço "sem produtividade" (D6).
 */
export function serviceLaborHours(
  service: Pick<CatalogService, 'key' | 'items'>,
  quantities: TakeoffQuantities,
): number | undefined {
  let soma: number | undefined;
  for (const item of service.items) {
    if (item.kind !== 'MAO_DE_OBRA' || !isHourUnit(item.unit)) continue;
    const { quantity } = resolveItemQuantity(item, service.key, quantities);
    if (quantity === undefined || !Number.isFinite(quantity)) continue;
    soma = (soma ?? 0) + quantity;
  }
  return soma === undefined ? undefined : arredondar(soma);
}

// ── A equipe (D5) ───────────────────────────────────────────────────────────────

/**
 * D5 — a equipe padrão de cada disciplina: quantos funcionários **ativos** da obra têm uma
 * função vinculada a ela (Mão de obra → disciplinas e funções).
 *
 * É o padrão, editável na geração: o vínculo diz quem **pode** executar, e o planejador
 * decide quantos executam. A disciplina sem ninguém não aparece — quem lê usa um (`crewOf`).
 */
export function linkedEmployeesByDiscipline(
  employees: readonly Employee[],
  links: readonly JobRoleDiscipline[],
): ReadonlyMap<string, number> {
  const indice = JobRoleDisciplineIndex.build(links);
  const contagem = new Map<string, number>();
  for (const employee of employees) {
    if (!isAllocatable(employee)) continue;
    for (const disciplineId of indice.disciplinesOf(employee.jobRoleId)) {
      contagem.set(disciplineId, (contagem.get(disciplineId) ?? 0) + 1);
    }
  }
  return contagem;
}

/** A equipe que a conta usa: a informada, ou a padrão — nunca menos de uma pessoa. */
export function crewOf(people: number | undefined): number {
  if (people === undefined || !Number.isFinite(people)) return 1;
  return Math.max(1, Math.trunc(people));
}

// ── A duração (RN-416) ──────────────────────────────────────────────────────────

/** O serviço tem produtividade? HH zero é índice não preenchido, e não "não leva tempo". */
export function hasLaborHours(laborHours: number | undefined): laborHours is number {
  return laborHours !== undefined && Number.isFinite(laborHours) && laborHours > 0;
}

/** Folga da divisão: 90 HH ÷ (1 × 9 h) são 10 dias, e não 11 por causa do ponto flutuante. */
const FOLGA = 1e-9;

/**
 * RN-416 — quantos dias úteis o serviço leva no pavimento.
 *
 * ```text
 * dias úteis = teto(HH ÷ (pessoas × jornada do dia)), no mínimo 1
 * ```
 *
 * A jornada é a do dia **padrão** do calendário da obra (`dailyQuarterHours`, em quartos de
 * hora — 36 é 9 h). O sábado e o feriado não entram aqui: eles mudam **quais** dias contam,
 * e é a cadeia de datas (`endAfterWorkingDays`) que os pula ou os usa.
 *
 * Sem HH — ou com zero — o serviço leva um dia útil por pavimento, e vai para a lista "sem
 * produtividade" (D6). O calendário sem jornada também dá um dia: dividir por zero não é
 * duração.
 */
export function durationInWorkdays(
  laborHours: number | undefined,
  crew: number,
  calendar: Pick<WorkCalendar, 'dailyQuarterHours'>,
): number {
  if (!hasLaborHours(laborHours)) return 1;
  const horasDoDia = calendar.dailyQuarterHours / 4;
  if (!Number.isFinite(horasDoDia) || horasDoDia <= 0) return 1;
  return Math.max(1, Math.ceil(laborHours / (crewOf(crew) * horasDoDia) - FOLGA));
}

// ── A proposta: a linha de balanço (RN-417, RN-420) ─────────────────────────────

/** Uma linha a encadear: o pavimento × serviço, o HH e a equipe. */
export interface LineOfBalanceLine {
  readonly floorId: FloorId;
  readonly serviceId: ServiceId;
  readonly laborHours?: number;
  readonly crew: number;
}

/** O item que já existe: as datas dele ficam, e a cadeia continua a partir delas. */
export interface FixedItemDates {
  readonly plannedStart: string;
  readonly plannedEnd: string;
  readonly wbsCode?: string;
}

export interface LineOfBalanceInput {
  /** A data de início da obra no quantitativo — o primeiro item começa nela (RN-417). */
  readonly startDate: string;
  /**
   * A ordem dos pavimentos, de quem sobe primeiro a quem sobe por último. É também o número
   * do pavimento na EDT sintética. O pavimento de uma linha que não está aqui vai para o fim.
   */
  readonly floorOrder: readonly string[];
  /**
   * A ordem do catálogo dos serviços — a disciplina e, dentro dela, o serviço. É a ordem
   * dentro do pavimento e o número do serviço na EDT sintética.
   */
  readonly serviceOrder: readonly string[];
  readonly lines: readonly LineOfBalanceLine[];
  /** Os itens que já existem, por `takeoffLineKey`: as datas e a EDT deles ficam. */
  readonly existing: ReadonlyMap<string, FixedItemDates>;
  /** As EDT que os outros itens da obra já usam — a sintética não repete nenhuma. */
  readonly takenWbsCodes?: ReadonlySet<string>;
  readonly calendar: ResolvedCalendar;
}

export interface ProposedScheduleLine {
  readonly floorId: FloorId;
  readonly serviceId: ServiceId;
  /** A EDT do item: a sintética no novo, a que ele já tinha no que existe (pode faltar). */
  readonly wbsCode: string | undefined;
  /** As EDT das antecessoras na cadeia — vazias no item que já existe, que fica como está. */
  readonly predecessorWbsCodes: readonly string[];
  readonly start: string;
  readonly end: string;
  /** A duração que o HH e a equipe dão (RN-416) — no item que existe, só informativa. */
  readonly workdays: number;
  readonly crew: number;
  readonly existing: boolean;
  /** Sem HH: um dia útil e a lista "sem produtividade" (D6). */
  readonly withoutProductivity: boolean;
}

/** `Q.03.012` — a EDT sintética do pavimento 3 × serviço 12 (RN-417). */
export function takeoffWbsCode(floorNumber: number, serviceNumber: number): string {
  return `Q.${String(floorNumber).padStart(2, '0')}.${String(serviceNumber).padStart(3, '0')}`;
}

/** A posição de cada id na lista, e os de fora depois dela, na ordem em que aparecem. */
function posicoes(ordem: readonly string[], extras: readonly string[]): Map<string, number> {
  const mapa = new Map<string, number>();
  for (const id of ordem) if (!mapa.has(id)) mapa.set(id, mapa.size);
  for (const id of extras) if (!mapa.has(id)) mapa.set(id, mapa.size);
  return mapa;
}

function maiorData(a: string | undefined, b: string | undefined): string | undefined {
  if (a === undefined) return b;
  if (b === undefined) return a;
  return a >= b ? a : b;
}

/**
 * RN-417 — a proposta pela linha de balanço.
 *
 * - **Ordem:** os pavimentos na ordem informada; no pavimento, os serviços na ordem do
 *   catálogo.
 * - **Início:** o serviço S no pavimento P começa no próximo dia útil depois do maior entre o
 *   fim de S no pavimento anterior que tem S (a mesma equipe sobe) e o fim do serviço anterior
 *   em P. O item sem nenhum dos dois começa no primeiro dia útil a partir da data de início,
 *   e nenhum item novo começa antes dela.
 * - **Fim:** o início mais a duração em dias úteis (RN-416), pelo calendário da obra.
 * - **O item que já existe** mantém as datas e a EDT dele (RN-322), e a cadeia continua a
 *   partir do fim dele.
 * - **A EDT** do item novo é a sintética `Q.<pavimento>.<serviço>`, pelas posições nas duas
 *   ordens — a mesma entrada dá a mesma EDT. A que um outro item da obra já usa ganha um
 *   sufixo (`Q.03.012.2`), para que a predecessora aponte um item só.
 * - **As predecessoras** são as EDT dos dois de quem o item depende, como o arquivo as grava
 *   (RN-323) — a do item sem EDT não tem como entrar.
 *
 * A linha de base nasce igual à proposta (RN-420) — é a criação do item que a grava.
 */
export function proposeLineOfBalance(input: LineOfBalanceInput): ProposedScheduleLine[] {
  const pavimentos = posicoes(
    input.floorOrder,
    input.lines.map((linha) => linha.floorId),
  );
  const servicos = posicoes(
    input.serviceOrder,
    input.lines.map((linha) => linha.serviceId),
  );
  const ordenadas = [...input.lines].sort(
    (a, b) =>
      pavimentos.get(a.floorId)! - pavimentos.get(b.floorId)! ||
      servicos.get(a.serviceId)! - servicos.get(b.serviceId)!,
  );

  const inicioDaObra = firstWorkingDayFrom(input.calendar, BusinessDate.of(input.startDate));
  const usadas = new Set(input.takenWbsCodes ?? []);
  const ultimoDoServico = new Map<string, ProposedScheduleLine>();
  const ultimoDoPavimento = new Map<string, ProposedScheduleLine>();
  const propostas: ProposedScheduleLine[] = [];

  for (const linha of ordenadas) {
    const doServico = ultimoDoServico.get(linha.serviceId);
    const doPavimento = ultimoDoPavimento.get(linha.floorId);
    const workdays = durationInWorkdays(linha.laborHours, linha.crew, input.calendar.calendar);
    const fixo = input.existing.get(takeoffLineKey(linha.floorId, linha.serviceId));

    let proposta: ProposedScheduleLine;
    if (fixo !== undefined) {
      proposta = {
        floorId: linha.floorId,
        serviceId: linha.serviceId,
        wbsCode: fixo.wbsCode,
        predecessorWbsCodes: [],
        start: fixo.plannedStart,
        end: fixo.plannedEnd,
        workdays,
        crew: crewOf(linha.crew),
        existing: true,
        withoutProductivity: !hasLaborHours(linha.laborHours),
      };
    } else {
      const base = maiorData(doServico?.end, doPavimento?.end);
      const depoisDaBase =
        base === undefined
          ? inicioDaObra
          : nextWorkingDayAfter(input.calendar, BusinessDate.of(base));
      const inicio = depoisDaBase.isBefore(inicioDaObra) ? inicioDaObra : depoisDaBase;
      const fim = endAfterWorkingDays(input.calendar, inicio, workdays);

      const sintetica = takeoffWbsCode(
        pavimentos.get(linha.floorId)! + 1,
        servicos.get(linha.serviceId)! + 1,
      );
      let wbsCode = sintetica;
      for (let sufixo = 2; usadas.has(wbsCode); sufixo += 1) {
        wbsCode = `${sintetica}.${String(sufixo)}`;
      }
      usadas.add(wbsCode);

      const predecessorWbsCodes = [
        ...new Set(
          [doServico?.wbsCode, doPavimento?.wbsCode].filter(
            (codigo): codigo is string => codigo !== undefined && codigo !== wbsCode,
          ),
        ),
      ];
      proposta = {
        floorId: linha.floorId,
        serviceId: linha.serviceId,
        wbsCode,
        predecessorWbsCodes,
        start: inicio.toString(),
        end: fim.toString(),
        workdays,
        crew: crewOf(linha.crew),
        existing: false,
        withoutProductivity: !hasLaborHours(linha.laborHours),
      };
    }

    propostas.push(proposta);
    ultimoDoServico.set(linha.serviceId, proposta);
    ultimoDoPavimento.set(linha.floorId, proposta);
  }
  return propostas;
}

// ── A revisão do quantitativo (RN-419) ──────────────────────────────────────────

/** O item que o quantitativo gerou ou assumiu, com o retrato do que foi aplicado. */
export interface TrackedTakeoffItem {
  readonly itemId: ScheduleItemId;
  /** `takeoffLineKey` do pavimento da frente × serviço. */
  readonly key: string;
  readonly takeoffQuantity?: number;
  readonly takeoffLaborHours?: number;
  readonly takeoffMissing: boolean;
}

/** O que o quantitativo diz hoje de um pavimento × serviço. */
export interface CurrentTakeoffValues {
  readonly quantity?: number;
  readonly unit?: string;
  readonly laborHours?: number;
}

export interface TakeoffNumbers {
  readonly quantity?: number;
  readonly laborHours?: number;
}

export interface ChangedTakeoffItem {
  readonly itemId: ScheduleItemId;
  readonly key: string;
  readonly before: TakeoffNumbers;
  readonly after: CurrentTakeoffValues;
  /** Estava marcado como fora do quantitativo, e voltou. */
  readonly returned: boolean;
}

export interface ScheduleTakeoffDiff {
  readonly changed: readonly ChangedTakeoffItem[];
  /** Pavimento × serviço que o quantitativo tem e nenhum item gerado acompanha. */
  readonly added: readonly string[];
  /** Os itens cujo pavimento × serviço saiu do quantitativo — ainda não marcados. */
  readonly gone: readonly { readonly itemId: ScheduleItemId; readonly key: string }[];
}

function mesmoNumero(a: number | undefined, b: number | undefined): boolean {
  if (a === undefined || b === undefined) return a === b;
  return arredondar(a) === arredondar(b);
}

/**
 * RN-419 — o que mudou entre o quantitativo de hoje e o que os itens guardaram.
 *
 * - **Mudou:** o item acompanhado cuja quantidade ou HH guardados (`takeoff_*`) diferem dos
 *   de hoje — e o que estava marcado como fora e voltou.
 * - **Novo:** o pavimento × serviço do quantitativo que nenhum item acompanhado cobre. Ele só
 *   é avisado: entra pela geração, que mantém as datas de quem já existe.
 * - **Sumiu:** o item acompanhado cujo pavimento × serviço o quantitativo não tem mais. O que
 *   já está marcado não conta de novo.
 *
 * A comparação é nas casas que o banco guarda (`SCHEDULE_ITEM_DECIMALS`): a dízima de uma
 * conta refeita não é mudança.
 */
export function diffScheduleTakeoff(
  tracked: readonly TrackedTakeoffItem[],
  current: ReadonlyMap<string, CurrentTakeoffValues>,
): ScheduleTakeoffDiff {
  const changed: ChangedTakeoffItem[] = [];
  const gone: { itemId: ScheduleItemId; key: string }[] = [];
  const cobertas = new Set<string>();

  for (const item of tracked) {
    cobertas.add(item.key);
    const hoje = current.get(item.key);
    if (hoje === undefined) {
      if (!item.takeoffMissing) gone.push({ itemId: item.itemId, key: item.key });
      continue;
    }
    const mudou =
      !mesmoNumero(item.takeoffQuantity, hoje.quantity) ||
      !mesmoNumero(item.takeoffLaborHours, hoje.laborHours);
    if (mudou || item.takeoffMissing) {
      changed.push({
        itemId: item.itemId,
        key: item.key,
        before: {
          ...(item.takeoffQuantity === undefined ? {} : { quantity: item.takeoffQuantity }),
          ...(item.takeoffLaborHours === undefined ? {} : { laborHours: item.takeoffLaborHours }),
        },
        after: hoje,
        returned: item.takeoffMissing,
      });
    }
  }

  const added = [...current.keys()].filter((key) => !cobertas.has(key));
  return { changed, added, gone };
}

// ── O item (RN-418, RN-419, RN-420) ─────────────────────────────────────────────

export interface CreateTakeoffItemInput {
  readonly id: ScheduleItemId;
  readonly projectId: ProjectId;
  readonly activityId: ActivityId;
  readonly serviceId: ServiceId;
  readonly wbsCode: string | undefined;
  readonly predecessorWbsCodes: readonly string[];
  readonly start: string;
  readonly end: string;
  readonly values: CurrentTakeoffValues;
  readonly crewSize: number;
  readonly now: string;
  readonly userId?: UserId;
}

/**
 * O item que nasce do quantitativo (RN-415, RN-418, RN-420).
 *
 * A linha de base nasce igual à proposta — é a mesma criação do item do arquivo
 * (`createScheduleItem`, RN-322). A origem é `QUANTITATIVO`, o retrato `takeoff_*` é o número
 * de agora, e a equipe é a que deu a duração.
 */
export function createItemFromTakeoff(
  input: CreateTakeoffItemInput,
): Result<ScheduleItem, DomainError> {
  const criado = createScheduleItem({
    id: input.id,
    projectId: input.projectId,
    activityId: input.activityId,
    serviceId: input.serviceId,
    values: {
      plannedStart: input.start,
      plannedEnd: input.end,
      predecessorWbsCodes: input.predecessorWbsCodes,
      quantityProvisional: false,
      ...(input.wbsCode === undefined ? {} : { wbsCode: input.wbsCode }),
      ...(input.values.quantity === undefined ? {} : { quantity: input.values.quantity }),
      ...(input.values.unit === undefined ? {} : { unit: input.values.unit }),
      ...(input.values.laborHours === undefined ? {} : { laborHours: input.values.laborHours }),
    },
    now: input.now,
    ...(input.userId === undefined ? {} : { userId: input.userId }),
  });
  if (!criado.ok) return criado;
  return ok({
    ...criado.value,
    quantitySource: 'QUANTITATIVO',
    crewSize: crewOf(input.crewSize),
    ...(criado.value.quantity === undefined ? {} : { takeoffQuantity: criado.value.quantity }),
    ...(criado.value.laborHours === undefined
      ? {}
      : { takeoffLaborHours: criado.value.laborHours }),
  });
}

/** O item com a quantidade, a unidade e o HH do quantitativo — e só isso mudado. */
function comNumerosDoQuantitativo(item: ScheduleItem, values: CurrentTakeoffValues): ScheduleItem {
  const quantity = values.quantity === undefined ? undefined : arredondar(values.quantity);
  const laborHours = values.laborHours === undefined ? undefined : arredondar(values.laborHours);
  const unit = quantity === undefined ? undefined : values.unit;
  const {
    quantity: _quantidade,
    unit: _unidade,
    laborHours: _hh,
    takeoffQuantity: _retrato,
    takeoffLaborHours: _retratoHh,
    takeoffMissingAt: _marca,
    ...resto
  } = item;
  return {
    ...resto,
    quantitySource: 'QUANTITATIVO',
    // O número do quantitativo é medido, e não rateio: a marca de provisória sai (RN-324).
    quantityProvisional: false,
    ...(quantity === undefined ? {} : { quantity, takeoffQuantity: quantity }),
    ...(unit === undefined ? {} : { unit }),
    ...(laborHours === undefined ? {} : { laborHours, takeoffLaborHours: laborHours }),
  };
}

/** Mudou algum campo que a geração e a atualização escrevem? Datas e avanço nunca. */
function numerosDiferem(antes: ScheduleItem, depois: ScheduleItem): boolean {
  return (
    antes.quantitySource !== depois.quantitySource ||
    antes.quantity !== depois.quantity ||
    antes.unit !== depois.unit ||
    antes.laborHours !== depois.laborHours ||
    antes.quantityProvisional !== depois.quantityProvisional ||
    antes.fileQuantity !== depois.fileQuantity ||
    antes.fileLaborHours !== depois.fileLaborHours ||
    antes.takeoffQuantity !== depois.takeoffQuantity ||
    antes.takeoffLaborHours !== depois.takeoffLaborHours ||
    antes.takeoffMissingAt !== depois.takeoffMissingAt
  );
}

function carimbar(
  antes: ScheduleItem,
  candidato: ScheduleItem,
  now: string,
  userId: UserId | undefined,
): ScheduleItemChange {
  if (!numerosDiferem(antes, candidato)) return { item: antes, changed: false };
  const { updatedByUserId: _quem, ...resto } = candidato;
  const quem = userId ?? antes.updatedByUserId;
  return {
    item: { ...resto, updatedAt: now, ...(quem === undefined ? {} : { updatedByUserId: quem }) },
    changed: true,
  };
}

/**
 * RN-418 — o quantitativo assume o item que já existe para o pavimento × serviço.
 *
 * A quantidade e o HH passam a ser os do quantitativo, com a origem `QUANTITATIVO`. Na
 * primeira vez — o item ainda era do arquivo —, os números que ele tinha vão para
 * `fileQuantity` e `fileLaborHours`, para a tela mostrar a diferença (D2). Na segunda, o
 * número do arquivo já guardado fica.
 *
 * Datas, linha de base, EDT, predecessoras e avanço não mudam (RN-417: o item que já existe
 * mantém as datas). A equipe também não: as datas dele não saíram dela.
 */
export function takeOverItemFromTakeoff(
  item: ScheduleItem,
  values: CurrentTakeoffValues,
  now: string,
  userId?: UserId,
): ScheduleItemChange {
  const primeiraVez = item.quantitySource !== 'QUANTITATIVO';
  const fileQuantity = primeiraVez ? item.quantity : item.fileQuantity;
  const fileLaborHours = primeiraVez ? item.laborHours : item.fileLaborHours;
  const { fileQuantity: _arquivo, fileLaborHours: _arquivoHh, ...semArquivo } = item;
  const candidato: ScheduleItem = {
    ...comNumerosDoQuantitativo(semArquivo, values),
    ...(fileQuantity === undefined ? {} : { fileQuantity }),
    ...(fileLaborHours === undefined ? {} : { fileLaborHours }),
  };
  return carimbar(item, candidato, now, userId);
}

/**
 * RN-419 — aplica a revisão do quantitativo ao item: só a quantidade, a unidade e o HH, e o
 * retrato `takeoff_*` com eles. O item que estava marcado como fora volta sem a marca.
 *
 * Datas, linha de base e avanço não mudam, e o número do arquivo guardado também não.
 */
export function refreshItemFromTakeoff(
  item: ScheduleItem,
  values: CurrentTakeoffValues,
  now: string,
  userId?: UserId,
): ScheduleItemChange {
  return carimbar(item, comNumerosDoQuantitativo(item, values), now, userId);
}

/**
 * RN-419 — o pavimento × serviço saiu do quantitativo: o item fica, marcado.
 *
 * Nada mais muda — nem a quantidade, que continua dizendo o que foi planejado. Marcado já,
 * fica com a data da primeira marca.
 */
export function markItemTakeoffMissing(
  item: ScheduleItem,
  now: string,
  userId?: UserId,
): ScheduleItemChange {
  if (item.takeoffMissingAt !== undefined) return { item, changed: false };
  return carimbar(item, { ...item, takeoffMissingAt: now }, now, userId);
}
